import * as notify from 'src/services/notification.service';
import * as dnbGetterService from './dnbCra.getter.service';
import { DEFAULT_FIELD } from 'src/constants';
import {
  CraPreReqMapToCraDefaults,
  TypeDnBCodeToStandards,
  CraRiskScoreRanges,
  Overrides,
  OverrideDefaults,
  DefaultCraPreReqObj,
  OccupationStandardCodesToUI,
  CraWeightage,
  DefaultRiskRatingForMissingFields,
  CraRating,
  CraToRiskFactorDescription,
  ISO2,
  SCORE,
  JurisdictionForUI,
  OverrideFields,
  DefaultCraUI,
  DefaultCraComputationObj,
  DefaultCraUiDetailsPreReq,
  DefaultJurisdictionUIValue,
} from 'src/constants/cra.constants';
import {
  ICraInternalObject,
  ICraComputationObject,
  IActivityCodeRatingsObject,
  ICraDetailsForUI,
  IJurisdictionUI,
  IOccupationStandardCodesToUI,
  ICraJurisdiction,
  ICraDetailsPreReq,
} from '../types/ICra';
import { getHighestValueFromArray } from '../utils/getHighestValueFromArray';
import { SOURCE_PROVIDER } from 'src/constants/companyReport';
import { sortArrayofObjectsByKey } from 'src/utils/sortArrayofObjectsByKey';
import * as FormatAndSortUtils from 'src/utils/formatAndSortMethods';

// This is cra object that is pre-requisite for running calculations to compute CRA itself
let craObj: ICraInternalObject = Object.assign({}, DefaultCraPreReqObj);

let craComputationsObj: ICraComputationObject = {
  ...DefaultCraComputationObj,
};

let craDetailsForUI: ICraDetailsForUI = {
  ...DefaultCraUI,
};

let craUIDetailsPreReq: ICraDetailsPreReq = {
  ...DefaultCraUiDetailsPreReq,
};

/**
 * @summary This method directs the cra object creation according to switch case
 * It is used as a step function to get a validated cra object, or an error otherwise
 * @param provider determines the data source name
 * @param companyProfile is the generic object which data sources get mapped to
 */
export const mapCompanyProfileToCraObject = (provider: string, companyProfile: any) => {
  try {
    switch (provider) {
      case SOURCE_PROVIDER.D:
        return getCraObjectForDnBCompanyprofile(companyProfile);

      default:
        console.log('No mapping for provider is available for CRA calculation');
        return null;
    }
  } catch (error: any) {
    // console.log(error && error?.message);
    notify.error(error && error?.message);
    return error.message;
  }
};

/**
 * @summary This method validates and retrieves the required keys in the company profile, for the CRA object
 * If validation is successful, it gets the field values and adds them in the CRA object
 * If validation is unsuccessful it exits with error which can be seen as notification on screen as well
 * @param companyProfile
 * @returns craObj ICraInternalObject
 */
export const getCraObjectForDnBCompanyprofile = (companyProfile: any) => {
  try {
    if (!companyProfile || !Object.keys(companyProfile).length) {
      throw new Error('Missing or Empty Company profile object');
    }
    // check for industry codes availability
    const activityCodes = dnbGetterService.getActivityCodes(companyProfile);
    if (!activityCodes || activityCodes.length === 0) {
      craObj.activityCodes = null;
      console.warn('Missing or Empty industry codes');
    } else {
      craObj.activityCodes = activityCodes;
    }

    // check for country codes availability
    const countryCodes = dnbGetterService.getCountryCodes(companyProfile);
    if (!countryCodes || !Object.keys(countryCodes).length) {
      console.warn('Missing or Empty country codes');
      craObj.countries.countryCodes = null;
    } else {
      craObj.countries.countryCodes = countryCodes;
    }

    // check for country names
    const countryNames = dnbGetterService.getCountryNames(companyProfile);
    if (!countryNames || !Object.keys(countryNames).length) {
      console.warn('Missing or empty country names');
      craObj.countries.countryNames = null;
    } else {
      craObj.countries.countryNames = countryNames;
    }

    // check for nationalities
    const nationalities = dnbGetterService.getNationalities(companyProfile);
    if (!nationalities || !Object.keys(nationalities).length) {
      console.warn('Missing or empty country names');
      craObj.countries.nationalities = null;
    } else {
      craObj.countries.nationalities = nationalities;
    }

    // get customer type from compnay profile
    const customerType = dnbGetterService.getCustomerType(companyProfile);
    if (!customerType) {
      console.warn('Missing or Empty customer type');
      craObj.customerType = null;
    } else {
      craObj.customerType = {
        riskFactor: CraToRiskFactorDescription.customerType,
        source: 'Entity Type',
        attribute: customerType,
      };
    }
    return craObj;
  } catch (error: any) {
    console.error(error && error?.message);
    notify.error(error && error?.message);
  }
};

/**
 * This is responsible for computing the risk rating for DnB profile
 * If there are missing risk fields, it assigns default rating and score value to it
 * @param craPreReq
 * @param craDefaultValues
 * @returns { countryRatingsAboveMedium, craComputationsObj, highestCountryRisk }
 */
export const calculateRiskRatingForDnBCra = (
  craPreReq: ICraInternalObject,
  craDefaultValues: any,
) => {
  try {
    // go through the cra object fields collected and obtain their corresponding values from default cra files
    // product type
    let riskRating =
      craDefaultValues[CraPreReqMapToCraDefaults.product][craPreReq.product.attribute || ''];
    // assign default risk rating if unavailable
    if (!riskRating) {
      console.warn(
        `Could not find associated risk rating in Defaults for Product type ${craPreReq.product.attribute}`,
      );
      craComputationsObj.product = DefaultRiskRatingForMissingFields;
    } else {
      craComputationsObj.product = {
        ...craPreReq.product,
        riskFactor: CraToRiskFactorDescription.product,
        source: DEFAULT_FIELD,
        score: riskRating.SCORE,
        rating: riskRating.RATING,
      };
    }
    craDetailsForUI.product = { ...craComputationsObj.product };

    // delivery Channel
    riskRating =
      craDefaultValues[CraPreReqMapToCraDefaults.deliveryChannel][
        craPreReq.deliveryChannel.attribute || ''
      ];
    // assign default risk rating if unavailable
    if (!riskRating) {
      console.warn(
        `Could not find associated risk rating in Defaults for delivery channel ${craPreReq.deliveryChannel.attribute}`,
      );
      craComputationsObj.deliveryChannel = DefaultRiskRatingForMissingFields;
    } else {
      craComputationsObj.deliveryChannel = {
        ...craPreReq.deliveryChannel,
        riskFactor: CraToRiskFactorDescription.deliveryChannel,
        source: DEFAULT_FIELD,
        score: riskRating.SCORE,
        rating: riskRating.RATING,
      };
    }
    craDetailsForUI.deliveryChannel = { ...craComputationsObj.deliveryChannel };

    // get customer type
    // if its value is missing, assign the default risk rating to it
    if (!craPreReq.customerType) {
      craComputationsObj.customerType = DefaultRiskRatingForMissingFields;
      craDetailsForUI.customerType = DefaultRiskRatingForMissingFields;
    } else {
      riskRating =
        craDefaultValues[CraPreReqMapToCraDefaults.customerType][
          craPreReq.customerType.attribute || ''
        ];
      // assign default risk rating if unavailable
      if (!riskRating) {
        console.warn(
          `Could not find associated risk rating in Defaults for customer type ${craPreReq.customerType.attribute}`,
        );
        craComputationsObj.customerType = DefaultRiskRatingForMissingFields;
      } else {
        craComputationsObj.customerType = {
          ...craPreReq.customerType,
          score: riskRating.SCORE,
          rating: riskRating.RATING,
        };
      }
      craDetailsForUI.customerType = { ...craComputationsObj.customerType };
    }

    /** Get country risk / jurisdiction */
    craUIDetailsPreReq.jurisdiction = {};
    const { countryRatingsAboveMedium, highestCountryRisk } = getCountriesRiskRatingForCRA(
      craPreReq,
      craDefaultValues,
    );
    craUIDetailsPreReq.jurisdiction = { ...countryRatingsAboveMedium };

    // Map the highest country risk into the main computation object with its path
    if (highestCountryRisk && Object.keys(highestCountryRisk).length) {
      craComputationsObj.jurisdiction = { ...highestCountryRisk };
    } else {
      craComputationsObj.jurisdiction = DefaultRiskRatingForMissingFields;
    }

    /** Get Occupation_NOB Risk */
    // if activityCodes do not exist, assign default risk rating to it
    craUIDetailsPreReq.occupation = [];
    if (!craPreReq.activityCodes) {
      craComputationsObj.occupation = DefaultRiskRatingForMissingFields;
      craUIDetailsPreReq.occupation.push(DefaultRiskRatingForMissingFields);
    } else {
      const activityCodesRating = getActivityCodesRiskRatingForCRA(craPreReq, craDefaultValues);
      craUIDetailsPreReq.occupation.push(...(activityCodesRating || []));
      if (activityCodesRating && activityCodesRating.length) {
        // if activity Codes exist, return one with the highest risk
        const highestActivityCodeRisk = getHighestValueFromArray(activityCodesRating, SCORE);
        // if highest activity score exist, assign it to main cra computations object
        if (highestActivityCodeRisk && Object.keys(highestActivityCodeRisk).length) {
          craComputationsObj.occupation = {
            ...highestActivityCodeRisk,
          };
        }
      }
    }

    /** Overrides */
    // if there is valid hasAlerts value, pick the default values decided for it
    craComputationsObj.hasAlerts = craPreReq.hasAlerts;
    // hasAlerts will either be false or will have some values
    craDetailsForUI.overrides = craPreReq.hasAlerts
      ? [
          {
            riskFactor: CraToRiskFactorDescription.override,
            hasAlerts: craPreReq.hasAlerts,
            source: OverrideDefaults[OverrideFields.HasAlerts].description,
            attribute: OverrideDefaults[OverrideFields.HasAlerts].attribute,
            rating: OverrideDefaults[OverrideFields.HasAlerts].rating,
            score: OverrideDefaults[OverrideFields.HasAlerts].score,
          },
        ]
      : [];
    return { countryRatingsAboveMedium, craComputationsObj, highestCountryRisk, craDetailsForUI };
  } catch (error: any) {
    console.error(error && error?.message);
    notify.error(error && error?.message);
  }
};

/**
 * This method returns the final computation of the Risk score and rating
 * It goes through the risk factors and get their percentages
 * It then considers hasAlerts override for this object as well if it is present and then bumps up one level accordingly
 * @param craComputationsObj ICraComputationObject {[riskFactor]: {RATING: string, SCORE: number}, ...} for all risk factors
 * @returns { RATING: string, SCORE: number }
 */
export const getCraComputation = (craComputationsObj: ICraComputationObject) => {
  try {
    // console.log(craComputationsObj);
    let aggregatePercentages = 0;
    let rating = '';
    // Go thorugh the risk factors and calculate the cra according to their corresponding percentages
    for (let [riskFactor, riskrating] of Object.entries(craComputationsObj)) {
      // if field is part of Overrides, it should be ignored
      if (Overrides[riskFactor]) {
        continue;
      }
      // @ts-ignore
      aggregatePercentages += riskrating?.score * (CraWeightage[riskFactor] / 100);
    }

    // shorten to two decimal places
    let finalScore = Number(aggregatePercentages.toFixed(2));

    // get rating according to documentation: https://vodworks.atlassian.net/wiki/spaces/C/pages/2296872989/CRA+development+Phase+1+first+iteration#Minor-details-for-the-selected-Approach
    if (finalScore > CraRiskScoreRanges.LowMin && finalScore <= CraRiskScoreRanges.LowMax) {
      rating = CraRating.low;
    } else if (
      finalScore > CraRiskScoreRanges.MediumMin &&
      finalScore <= CraRiskScoreRanges.MediumMax
    ) {
      rating = CraRating.medium;
    } else if (
      finalScore > CraRiskScoreRanges.HighMin &&
      finalScore <= CraRiskScoreRanges.HighMax
    ) {
      rating = CraRating.high;
    } else if (
      finalScore > CraRiskScoreRanges.VeryHighMin &&
      finalScore <= CraRiskScoreRanges.VeryHighMax
    ) {
      rating = CraRating.VeryHigh;
    } else if (
      finalScore > CraRiskScoreRanges.SanctionedMin &&
      finalScore <= CraRiskScoreRanges.SanctionedMax
    ) {
      rating = CraRating.Sanctioned;
    } else if (
      finalScore > CraRiskScoreRanges.ProhibitedMin &&
      finalScore <= CraRiskScoreRanges.ProhibitedMax
    ) {
      rating = CraRating.Prohibited;
    }
    // Override: if it hasAlerts, then its final value should be changed
    return craComputationsObj.hasAlerts
      ? getOverRiddenRatingAndScore(rating, finalScore)
      : { rating: rating, score: finalScore };
  } catch (error: any) {
    console.error(error && error.message);
    notify.error(error && error.message);
  }
};

/**
 * This function is responsible for bumping up the value of risk and rating to one step above. If Applicable.
 * This should be only applied if the override flag(s) is set in the cra mapping
 * The default min value should be High
 * @param rating string
 * @param score number
 * @returns { rating, score }
 */
export const getOverRiddenRatingAndScore = (rating: string, score: number) => {
  let finalScore = score;
  console.log(`\t Override(s) found bumping up the values from ${rating} ${score}`);
  // bump up rating
  if (rating === CraRating.Prohibited) {
    // Since this is highest rating we dont do anything
    return { rating: rating, score: finalScore };
  } else if (rating === CraRating.Sanctioned) {
    // if rating is Sanctioned, Increase it to Prohibited and set corresponding min score
    rating = CraRating.Prohibited;
    finalScore = CraRiskScoreRanges.ProhibitedMin;
  } else if (rating === CraRating.VeryHigh) {
    // if rating is very high, Increase it to Sanctioned and set corresponding min score
    rating = CraRating.Sanctioned;
    finalScore = CraRiskScoreRanges.SanctionedMin;
  } else if (rating === CraRating.high) {
    // if rating is high, Increase it to Very High and set corresponding min score
    rating = CraRating.VeryHigh;
    finalScore = CraRiskScoreRanges.VeryHighMin;
  } else if (rating === CraRating.medium) {
    // if rating is medium, Increase it to High and set corresponding min score
    rating = CraRating.high;
    finalScore = CraRiskScoreRanges.HighMin;
  } else if (rating === CraRating.low) {
    // if rating is low. Increase it to Medium and set corresponding min score
    rating = CraRating.medium;
    finalScore = CraRiskScoreRanges.MediumMin;
  }
  return { rating, score: finalScore };
};

/**
 * This serves as a step function to get the following 3 things out of the Countries list we had gathered at this point
 * 1. all Countries Risk Rating i.e. a map from country codes and country names (from craPreReq) and corresponding Risk/Rating (from craDefaults)
 * 2. countries risk rating above or Equal to Medium Risk
 * 3. Highest Risk Score identified along with its path
 * @param craPreReq ICraInternalObject
 * @param craDefaultValues Default CRA JSON
 * @returns { allCountriesRiskRating, countryRatingsAboveMedium, highestCountryRisk }
 */
export const getCountriesRiskRatingForCRA = (
  craPreReq: ICraInternalObject,
  craDefaultValues: any,
) => {
  // 1. get ratings for countries using both countryCodes and countryNames from craPreReq object
  const allCountriesRiskRating = dnbGetterService.getCountriesRiskRatings(
    craPreReq,
    craDefaultValues,
  );

  // if countries are not found, return the empty or null objects
  if (
    !allCountriesRiskRating ||
    typeof allCountriesRiskRating !== 'object' ||
    !Object.keys(allCountriesRiskRating).length
  ) {
    console.warn('No Country Codes or Country names found');
    return {
      allCountriesRiskRating: null,
      countryRatingsAboveMedium: {},
      highestCountryRisk: null,
    };
  }
  // 2. get country ratings for those that are above Medium >= 2 risk Score. We need this for UI eventually
  const countryRatingsAboveMedium =
    dnbGetterService.getCountriesAboveOrMediumRisk(allCountriesRiskRating);

  // 3. get highest risk score and its path from countries risks rating
  const highestCountryRisk = dnbGetterService.getHighestCountryRiskWithPath(allCountriesRiskRating);
  return { allCountriesRiskRating, countryRatingsAboveMedium, highestCountryRisk };
};

/**
 * Get corresponding risk rating for activity Codes
 * @param craPreReq
 * @param craDefaultValues
 * @returns
 */
export const getActivityCodesRiskRatingForCRA = (
  craPreReq: ICraInternalObject,
  craDefaultValues: any,
) => {
  try {
    let activityCodesRatings: IActivityCodeRatingsObject[] = [];
    const { activityCodes } = craPreReq;
    if (!activityCodes || !activityCodes.length) {
      console.log('Invalid or missing activity Codes passed to getActivityCodesRiskRatingForCRA');
      return;
    }
    for (let activityCode of activityCodes) {
      // make sure activity code has valid fields inside it
      if (
        activityCode &&
        Object.keys(activityCode).length &&
        activityCode.typeDnBCode &&
        activityCode.code
      ) {
        const correspondingStandard = TypeDnBCodeToStandards[activityCode.typeDnBCode];
        if (!correspondingStandard) {
          console.log(`Could not find a corresponding Standards for ${activityCode.typeDnBCode}`);
          continue;
        }
        // if corresponding JSON exist for standards, fetch its Risk/Rating using code field
        const activityCodeRating = craDefaultValues[correspondingStandard][activityCode.code];
        // if the corresponding rating is not found, this should be logged
        if (!activityCodeRating || !Object.keys(activityCodeRating).length) {
          console.warn(
            `Could not find corresponding entry in ${correspondingStandard} for code ${activityCode.code} (${activityCode.typeDnBCode}) `,
          );
        } else {
          // else add it to the list
          activityCodesRatings.push({
            riskFactor: CraToRiskFactorDescription.activityCodes,
            // @ts-ignore
            source: (OccupationStandardCodesToUI as IOccupationStandardCodesToUI)[
              correspondingStandard
            ],
            attribute: activityCode.description,
            key: activityCode.code,
            score: activityCodeRating.SCORE,
            rating: activityCodeRating.RATING,
          });
        }
      } else {
        console.error('Error: Invalid activity code passed');
      }
    }
    return activityCodesRatings;
  } catch (error: any) {
    console.log(error && error?.message);
    notify.error(error && error?.message);
  }
};

/**
 * formats the cra details for ui as per ticket 1445, 1447
 * @param craDetailsForUI ICraDetailsForUI
 * return craDetailsForUI ICraDetailsForUI
 */
export const formatCraDetailsForUI = (craDetailsForUI: ICraDetailsForUI) => {
  try {
    craDetailsForUI.occupation = formatAndSortOccupationForUI(craUIDetailsPreReq.occupation);
    craDetailsForUI.jurisdiction = formatAndSortJurisdictionForUI(craUIDetailsPreReq.jurisdiction);
    return craDetailsForUI;
  } catch (error: any) {
    console.error(error && error.message);
  }
};

/**
 * Sorts, formats and filters jursidiction to be ready for the UI
 * @param occupation key: [{value},...]
 * @returns formatted, sorted and filetered occupation object as per the UI requirements
 */
export const formatAndSortOccupationForUI = (occupation: any) => {
  // change occupation array to object key values
  occupation = FormatAndSortUtils.convertArraytoObjectValue(occupation, 'source');

  // sort these unique values inside nested fields by score
  occupation = FormatAndSortUtils.sortNestedFields(occupation, SCORE);

  // keep only the highest values inside these sorted nested fields
  occupation = FormatAndSortUtils.keepHighestValueInNestedArrays(occupation, SCORE);

  // flatten occupation and send it to sorting method
  const flattenedSortedArray = sortArrayofObjectsByKey(Object.values(occupation).flat(), SCORE);

  // @ts-ignore unflat them according to the UI heirarchy
  occupation = FormatAndSortUtils.convertArraytoObjectValue(flattenedSortedArray, 'source');
  return occupation;
};

/**
 * Sorts, formats and filters jursidiction to be ready for the UI
 * @param jurisdiction key: [{value}]
 * @returns formatted, sorted and filetered jurisdiction object as per the UI requirements
 */
export const formatAndSortJurisdictionForUI = (jurisdiction: ICraJurisdiction) => {
  // keep only unique jurisdictions in each of the nested field of jurisdiction
  let jurisdictionMap: any = {};
  jurisdictionMap = FormatAndSortUtils.removeRepeatedElementsInNestedObjectArrays(
    jurisdiction,
    ISO2,
  );

  // sort these unique values inside nested fields by score
  jurisdictionMap = FormatAndSortUtils.sortNestedFields(jurisdictionMap, SCORE);

  // keep only the highest values inside these sorted nested fields

  jurisdictionMap = FormatAndSortUtils.keepHighestValueInNestedArrays(jurisdictionMap, SCORE);

  // flatten jurisdiction and send it to sorting method
  const flattenedSortedJurisdiciton = sortArrayofObjectsByKey(
    Object.values(jurisdictionMap).flat(),
    SCORE,
  );

  // @ts-ignore unflat them according to the UI heirarchy
  jurisdictionMap = arrangeJurisdictionValues(flattenedSortedJurisdiciton);

  return jurisdictionMap;
};

/**
 * This expects a flattened array of objects, in this case of Jurisdictions, such that it does not have any nested properties
 * It cateogorises them according to the UI requirement.
 * @param flattenedSortedJurisdiciton [{},{}, {}]
 * @returns organised jurisdiction for UI IJurisdictionUI
 */
export const arrangeJurisdictionValues = (flattenedSortedJurisdiciton: any) => {
  try {
    let arrangedJurisdiction: IJurisdictionUI = {};
    for (let item of flattenedSortedJurisdiciton) {
      // company information
      if (item?.source.includes(JurisdictionForUI.sourceFields.Addresses)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.Addresses;
        if (
          !Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.CompanyInformation)
        ) {
          arrangedJurisdiction[
            JurisdictionForUI.displayText.CompanyInformation as keyof IJurisdictionUI
          ] = [];
        }
        arrangedJurisdiction[
          JurisdictionForUI.displayText.CompanyInformation as keyof IJurisdictionUI
        ].push(item);
      }

      if (item?.source.includes(JurisdictionForUI.sourceFields.OtherAddresses)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.OtherAddresses;
        if (
          !Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.CompanyInformation)
        ) {
          arrangedJurisdiction[
            JurisdictionForUI.displayText.CompanyInformation as keyof IJurisdictionUI
          ] = [];
        }
        arrangedJurisdiction[
          JurisdictionForUI.displayText.CompanyInformation as keyof IJurisdictionUI
        ].push(item);
      }

      if (item?.source.includes(JurisdictionForUI.sourceFields.TradingAddresses)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.TradingAddresses;
        if (
          !Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.CompanyInformation)
        ) {
          arrangedJurisdiction[
            JurisdictionForUI.displayText.CompanyInformation as keyof IJurisdictionUI
          ] = [];
        }
        arrangedJurisdiction[
          JurisdictionForUI.displayText.CompanyInformation as keyof IJurisdictionUI
        ].push(item);
      }

      // shareholders
      if (item?.source.includes(JurisdictionForUI.sourceFields.Shareholders)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.Shareholders;

        if (!Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.Shareholders)) {
          arrangedJurisdiction[
            JurisdictionForUI.displayText.Shareholders as keyof IJurisdictionUI
          ] = [];
        }
        arrangedJurisdiction[
          JurisdictionForUI.displayText.Shareholders as keyof IJurisdictionUI
        ].push(item);
      }

      // directors
      if (item?.source.includes(JurisdictionForUI.sourceFields.Directors)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.Directors;

        if (!Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.Directors)) {
          arrangedJurisdiction[JurisdictionForUI.displayText.Directors as keyof IJurisdictionUI] =
            [];
        }
        arrangedJurisdiction[JurisdictionForUI.displayText.Directors as keyof IJurisdictionUI].push(
          item,
        );
      }

      // beneficial owners
      if (item?.source.includes(JurisdictionForUI.sourceFields.BeneficialOwners)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.UBO;

        if (!Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.UBO)) {
          arrangedJurisdiction[JurisdictionForUI.displayText.UBO as keyof IJurisdictionUI] = [];
        }
        arrangedJurisdiction[JurisdictionForUI.displayText.UBO as keyof IJurisdictionUI].push(item);
      }

      /* Commenting Onwrship and Extended Group structure for now for first iteration
      //ownershipStructure
      if (item?.source.includes(JurisdictionForUI.sourceFields.OwnershipStructure)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.OwnershipStructure;

        if (
          !Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.OwnershipStructure)
        ) {
          arrangedJurisdiction[
            JurisdictionForUI.displayText.OwnershipStructure as keyof IJurisdictionUI
          ] = [];
        }
        arrangedJurisdiction[
          JurisdictionForUI.displayText.OwnershipStructure as keyof IJurisdictionUI
        ].push(item);
      }

      //extendedGroupStructure
      if (item?.source.includes(JurisdictionForUI.sourceFields.ExtendedGroupStructure)) {
        item.key = item.source;
        item.source = JurisdictionForUI.displayText.ExtendedGroupStructure;

        if (
          !Object.hasOwn(arrangedJurisdiction, JurisdictionForUI.displayText.ExtendedGroupStructure)
        ) {
          arrangedJurisdiction[
            JurisdictionForUI.displayText.ExtendedGroupStructure as keyof IJurisdictionUI
          ] = [];
        }
        arrangedJurisdiction[
          JurisdictionForUI.displayText.ExtendedGroupStructure as keyof IJurisdictionUI
        ].push(item);
      }
      */
    }

    // Flatten jurisdictions keys as per UI requirements
    for (let [key, value] of Object.entries(arrangedJurisdiction)) {
      arrangedJurisdiction[key as keyof IJurisdictionUI] = {
        riskFactor: value[0].riskFactor,
        key: value[0].key,
        rating: value[0].rating,
        score: value[0].score,
        source: value[0].source,
        attribute: Array.from(new Set(value.map((item: any) => item.attribute))).join(', '),
      };
    }

    // check for unavailable fields and cater accordingly
    arrangedJurisdiction = adjustMissingJurisdictionValues(arrangedJurisdiction);
    return arrangedJurisdiction;
  } catch (error: any) {
    console.error(error && error.message);
  }
};

export const clearFields = () => {
  craDetailsForUI = { ...DefaultCraUI };
  craObj = Object.assign({}, DefaultCraPreReqObj);
  craComputationsObj = Object.assign({}, DefaultCraComputationObj);
  craUIDetailsPreReq = Object.assign({}, DefaultCraUiDetailsPreReq);
};

/**
 * If there are missing display fields like shareholders, directors etc.
 * Show the default values for that unavailable object
 * @param arrangedJurisdiction IJurisdictionUI
 * @returns arrangedJurisdiction
 */
export const adjustMissingJurisdictionValues = (arrangedJurisdiction: IJurisdictionUI) => {
  for (let value of Object.values(JurisdictionForUI.displayFields)) {
    if (!arrangedJurisdiction[value as keyof IJurisdictionUI]) {
      arrangedJurisdiction[value as keyof IJurisdictionUI] = {
        ...DefaultJurisdictionUIValue,
        source: value,
      };
    }
  }
  return arrangedJurisdiction;
};
